/**
 * Passport / Year in Review Types
 * Shared between web and mobile
 */

export interface PassportArtist {
	id: string;
	name: string;
	imageUrl: string | null;
	seenCount: number;
	avgRating: number | null;
}

export interface PassportFestival {
	id: string;
	name: string;
	posterUrl: string | null;
	startDate: string;
	endDate: string | null;
	setsSeenCount: number;
	rating: number | null;
}

export interface PassportShow {
	id: string;
	title: string;
	posterUrl: string | null;
	artistImageUrl: string | null; // Fallback when posterUrl is null
	date: string;
	artistName: string | null;
	rating: number | null;
	venueName: string | null;
	location: string | null; // "City, State" or "City, Country"
}

export interface PassportGenre {
	name: string;
	count: number;
	percentage: number;
}

export interface PassportSet {
	id: string;
	artistName: string;
	artistImageUrl: string | null;
	setDisplayName: string; // Full display name with B2B/pres formatting
	festivalName: string;
	startTime: string; // ISO datetime
	rating: number | null;
	loved: boolean;
}

export interface MonthlyActivity {
	month: number; // 1-12
	monthLabel: string; // "Jan", "Feb", etc.
	festivalCount: number; // Days at festivals in this month (multi-day festivals contribute multiple days)
	showCount: number; // Days at shows in this month (1 per show)
	setCount: number; // Legacy, may be unused
}

export type PassportScope = "all" | "festivals" | "shows";

export interface YearInReviewData {
	// Metadata
	year: number;
	userId: string;
	username: string;
	userAvatar: string | null;
	generatedAt: string;
	scope: PassportScope;

	// Summary stats (for Cover card)
	totalFestivals: number;
	totalShows: number;
	totalSetsSeenLive: number;
	totalArtistsSeenLive: number;

	// Top 5 Festivals (sorted by sets seen at that festival)
	topFestivals: PassportFestival[];

	// All Festivals (chronological, max 12 for "festivals-all" card)
	allFestivals: PassportFestival[];

	// Top 5 Shows (sorted by attendance/date)
	topShows: PassportShow[];

	// All Shows (chronological, for "shows-all" cards)
	allShows: PassportShow[];

	// Top 5 Artists (saw live across festivals + shows)
	topArtistsSawLive: PassportArtist[];

	// All Artists saw live (sorted by seenCount desc, then alphabetical) - for grid card
	allArtistsSawLive: PassportArtist[];

	// Top 5 Artists (most streamed - sawStream count)
	topArtistsStreamed: PassportArtist[];

	// Top 5 Genres (from artists seen live)
	topGenres: PassportGenre[];

	// Top 8 Sets (by rating, then loved, then chronological)
	topSets: PassportSet[];

	// Monthly activity (for Timeline card)
	activityByMonth: MonthlyActivity[];

	// Upcoming festivals (marked "going" for next year) - for "upcoming" final card
	upcomingFestivals: PassportFestival[];

	// Has any data to show?
	hasData: boolean;
}

/**
 * Card types for the passport
 * Used for rendering and navigation
 * festivals-all-{n} and shows-all-{n} support pagination
 */
export type PassportCardType =
	| "cover"
	| "festivals"
	| "festivals-all"
	| `festivals-all-${number}`
	| "top-sets"
	| "shows"
	| "shows-all"
	| `shows-all-${number}`
	| "artists"
	| "artists-all"
	| "streamed"
	| "genres"
	| "timeline"
	| "upcoming";

/** Number of items to show per all-list page */
export const FESTIVALS_PER_PAGE = 8;
export const SHOWS_PER_PAGE = 7;
/** Artists grid: 6 columns x 10 rows = 60 artists max on one card */
export const ARTISTS_GRID_COLS = 6;
export const ARTISTS_GRID_ROWS = 10;
export const ARTISTS_PER_GRID = ARTISTS_GRID_COLS * ARTISTS_GRID_ROWS;

/**
 * Configuration for each passport card
 */
export interface PassportCardConfig {
	type: PassportCardType;
	title: string;
	enabled: boolean; // Whether to show this card (based on data availability)
}
